import api from '@crm-src/lib/api';
import { saveBlob } from '../../lib/download';

// Base API response interfaces
interface ApiResponse<T> {
  data: T;
  message?: string;
  status?: string;
}

interface PaginatedResponse<T> {
  data: T[];
  total_count: number;
  meta: {
    pagination: {
      current_page: number;
      total_pages: number;
      total_count: number;
      per_page: number;
    };
  };
}

// Contact interface
export interface Contact {
  id: number;
  name: string | null;
  surname: string | null;
  full_name: string;
  display_name: string;
  company: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  job_title: string | null;
  notes: string | null;
  active: boolean;
  // Industry: inherited from the parent prospect/customer for a mirrored
  // contact (unless overridden), or set manually for a standalone one. Drives
  // the Contacts industry filter + the campaign "by industry" segment.
  industry: string | null;
  industry_overridden: boolean;
  // Mirror provenance. "mirrored" rows are projected from a CustomerContact
  // (person on a prospect/customer) and managed at that source — the row
  // deep-links via customer_id and treats core fields as read-only.
  source: 'mirrored' | 'manual';
  customer_contact_id: number | null;
  customer_id: number | null;
  user: {
    id: number;
    full_name: string;
    email: string;
  } | null;
  created_at: string;
  updated_at: string;
}

// Contact form data
export interface ContactFormData {
  name?: string;
  surname?: string;
  company?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  job_title?: string;
  notes?: string;
  active?: boolean;
  industry?: string;
}

/**
 * Request params for the contacts list.
 *
 * Exported because `contacts-page-query.ts` proves at COMPILE time that every
 * facet key `CONTACTS_TABLE` declares is a param this interface carries — i.e.
 * one `Api::V1::ContactsController#filterable` actually implements. Without
 * that, a key that exists only in the config compiles fine and fails at runtime
 * as a param the server drops.
 *
 * `dir` is the canonical direction param (`Sortable` also accepts the legacy
 * `direction` / `sort_direction` spellings, which is why the old one is still
 * here for the callers that send it).
 */
export interface ContactFilters {
  search?: string;
  company?: string;
  active?: boolean;
  /** Facet: one or more canonical industry labels, comma-separated. */
  industry?: string;
  /** Facet: `active` / `inactive` — the table's name for `contacts.active`. */
  status?: string;
  /** Facet: owner user ids, comma-separated ("Created By" in the table). */
  created_by_id?: string;
  /** Facet: one reseller company's contacts. Drives the Contacts panel on the
   * reseller profile — a FILTER rather than a nested endpoint, so that panel
   * inherits this action's search, sorting and pagination instead of a fourth
   * copy of them. */
  reseller_id?: string | number;
  sort?: string;
  dir?: 'asc' | 'desc';
  direction?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
  show_all?: boolean;
}

// Contacts API class
export class ContactsAPI {
  // Get all contacts with filters
  static async getContacts(filters?: ContactFilters): Promise<PaginatedResponse<Contact>> {
    const params = new URLSearchParams();
    
    if (filters?.search) params.append('search', filters.search);
    if (filters?.company) params.append('company', filters.company);
    if (filters?.industry) params.append('industry', filters.industry);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.created_by_id) params.append('created_by_id', filters.created_by_id);
    if (filters?.reseller_id) params.append('reseller_id', String(filters.reseller_id));
    if (filters?.active !== undefined) params.append('active', filters.active.toString());
    if (filters?.sort) params.append('sort', filters.sort);
    if (filters?.dir) params.append('dir', filters.dir);
    if (filters?.direction) params.append('direction', filters.direction);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());
    if (filters?.show_all !== undefined) params.append('show_all', filters.show_all.toString());
    
    const response = await api.get(`/contacts?${params.toString()}`);
    return response.data;
  }

  // Industries that actually have EMAILABLE contacts (the set the campaign "By
  // Industry" segment sends to), each with a count. Drives the composer's industry
  // picker off real stored labels — no hardcoded list, no empty/mismatched options.
  static async getIndustries(): Promise<{ industry: string; count: number }[]> {
    const response = await api.get('/contacts/industries');
    return response.data?.data ?? [];
  }

  // Get a single contact
  static async getContact(id: number): Promise<ApiResponse<Contact>> {
    const response = await api.get(`/contacts/${id}`);
    return response.data;
  }
  
  // Create a new contact
  static async createContact(data: ContactFormData): Promise<ApiResponse<Contact>> {
    const response = await api.post('/contacts', { contact: data });
    return response.data;
  }
  
  // Update a contact
  static async updateContact(id: number, data: ContactFormData): Promise<ApiResponse<Contact>> {
    const response = await api.patch(`/contacts/${id}`, { contact: data });
    return response.data;
  }
  
  // Delete a contact
  static async deleteContact(id: number): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete(`/contacts/${id}`);
    return response.data;
  }

  // Bulk delete contacts
  static async bulkDeleteContacts(contactIds: number[]): Promise<ApiResponse<{ message: string; deleted_count: number; requested_count: number }>> {
    const response = await api.post('/contacts/bulk_delete', { contact_ids: contactIds });
    return response.data;
  }
  
  // Import contacts from CSV
  static async importContacts(file: File): Promise<ApiResponse<{ imported: number; errors?: string[]; message?: string }>> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/contacts/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
  
  // Export contacts to Excel
  static async exportContacts(showAll?: boolean): Promise<void> {
    const params = new URLSearchParams();
    if (showAll !== undefined) params.append('show_all', showAll.toString());
    
    const response = await api.get(`/contacts/export?${params.toString()}`, {
      responseType: 'blob',
    });
    
    // Native OS Save-As dialog in the desktop app; browser download on the web.
    await saveBlob(
      `contacts-${new Date().toISOString().split('T')[0]}.xlsx`,
      new Blob([response.data]),
    );
  }
}

export default ContactsAPI;
